/**
 * The room's frame, under the focus: what this market is in its own words and
 * the axes it sorts on. Written by the cartographer, editable by hand,
 * versioned; a proposal (something taken away) waits here for a yes or a no,
 * and any earlier version can be brought back with one click.
 */
import { useState } from 'react';
import type { BoardroomFrame } from '../../../types';
import { axesText, axisLabel, changeSummary, isNoOpProposal, parseAxes, specDiff } from './frameAxes';
import { seatRole, SeatRole } from '../plainWords';

const VOCAB: { key: keyof BoardroomFrame['vocab']; label: string }[] = [
  { key: 'market', label: 'market' },
  { key: 'player', label: 'players are' },
  { key: 'product', label: 'a product is' },
  { key: 'feature', label: 'a feature is' },
  { key: 'pricing', label: 'pricing is' },
];

/** "automatic, self-service, full-service" as three chips; a sentence stays one. */
export function meaningChips(meaning: string | null | undefined): string[] {
  const m = (meaning ?? '').trim();
  if (!m) return ['-'];
  const parts = m.split(/\s*,\s*/).filter(Boolean);
  return parts.length > 1 && parts.every(p => p.split(/\s+/).length <= 4) ? parts : [m];
}

export default function FrameLine({
  frame,
  proposals,
  history,
  onWrite,
  onUse,
  onAccept,
  onDismiss,
}: {
  frame: BoardroomFrame | null;
  proposals: BoardroomFrame[];
  history: BoardroomFrame[];
  onWrite: (body: { vocab: Record<string, string>; axes: { dimension: string; meaning: string }[]; reason?: string }) => Promise<void>;
  onUse: (frameId: number) => Promise<void>;
  onAccept: (frameId: number) => Promise<void>;
  onDismiss: (frameId: number) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [vocab, setVocab] = useState<Record<string, string>>({});
  const [axes, setAxes] = useState('');
  const [busy, setBusy] = useState(false);
  const shown = history.filter(h => !h.unchanged && !h.dismissed);

  const startEdit = () => {
    setVocab({ ...(frame?.vocab ?? { market: '', player: '', product: '', feature: '', pricing: '' }) });
    setAxes(frame ? axesText(frame.axes) : '');
    setEditing(true);
  };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onWrite({ vocab, axes: parseAxes(axes), reason: 'set by hand' });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-xs min-w-0" data-testid="frame-line">
      {/* THE MARKET IS THE HEADLINE. It used to be one run-on line — a FRAME
          eyebrow, the market, the players, the counts, the provenance and the
          controls — and then the same market and players again in the detail
          underneath. Now: what the market is, who the players are, and the
          controls; the provenance and the counts one quiet line below. */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {frame ? (
            <>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{frame.vocab.market || 'this market'}</div>
              <div className="text-gray-500 dark:text-gray-400">players are {frame.vocab.player || '-'}</div>
            </>
          ) : (
            <span className="text-gray-400 italic">not set up yet · the sorter writes it on the first dig</span>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-3">
          <button type="button" onClick={startEdit} className="text-indigo-600 hover:underline dark:text-indigo-300">
            edit
          </button>
          {shown.length > 1 && (
            <button type="button" onClick={() => setShowHistory(v => !v)} aria-expanded={showHistory} className="text-indigo-600 hover:underline dark:text-indigo-300">
              {showHistory ? 'hide history' : `history · ${shown.length}`}
            </button>
          )}
        </div>
      </div>
      {frame && (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-gray-400">
          <span data-testid="frame-axes" title={frame.axes.map(a => axisLabel(a.dimension)).join(', ')}>
            {frame.axes.length} tag group{frame.axes.length === 1 ? '' : 's'}
            {frame.specs?.length ? ` · ${frame.specs.length} column${frame.specs.length === 1 ? '' : 's'}` : ''}
          </span>
          {frame.since && (
            <span data-testid="frame-by">
              · {new Date(frame.since).toLocaleDateString()} · by {seatRole(frame.written_by)}
              {frame.ratified_by && frame.ratified_by !== frame.written_by && ` · checked by ${seatRole(frame.ratified_by)}`}
            </span>
          )}
        </div>
      )}

      {proposals.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5" data-testid="frame-proposals">
          {proposals.map(p => {
            /* NOTHING TO ACCEPT. A pass that read every phrase and landed on
               the frame already standing is a RESULT — "we checked, it holds
               up" — and dressing it as a pending decision asks you to approve
               a change that does not exist, at the cost of a full re-fit. The
               backend no longer raises these; the ones already on the board
               still have to be told the truth and got out of the way. */
            /* BELT AND BRACES. If this test were ever wrong in the eager
               direction it would strip the Accept button off a real proposal
               and leave it unacceptable — a far worse failure than showing a
               card for a no-op. So it must ALSO be true that the summary can
               name nothing: any change either test can see keeps the buttons. */
            if (isNoOpProposal(frame, p)) {
              return (
                /* NOT AMBER, BUT NOT INVISIBLE EITHER. Draining it to grey on
                   a grey band made it read as absent — the card was on screen
                   and went unseen, so the room looked like it had silently
                   dropped the proposal. It is a finished check, not a pending
                   decision, and that is a different thing from nothing: it is
                   still sitting there until somebody clears it. The tick says
                   which of the two it is; the weight says it is still here. */
                <li key={p.id} className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50/50 px-2 py-1 dark:border-emerald-800 dark:bg-emerald-900/10">
                  <span className="shrink-0 text-emerald-600 dark:text-emerald-400">✓</span>
                  <span className="min-w-0 flex-1 text-gray-700 dark:text-gray-200">
                    <strong className="font-medium">{SeatRole(p.written_by)}</strong>
                    {' checked the setup - nothing to change.'}
                    {p.reason && <span className="text-gray-500 dark:text-gray-400"> {p.reason}</span>}
                  </span>
                  <button type="button" onClick={() => onDismiss(p.id)} className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-gray-600 hover:bg-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
                    Clear
                  </button>
                </li>
              );
            }
            return (
            /* THE DECISION AND THE THING BEING DECIDED, TOGETHER. This was one
               run-on line — who proposes, what changes, the players, the whole
               reason sentence, and the status — joined by middots at one
               weight, with Accept a thousand pixels to the right of where the
               sentence began. You had to read to the end of a paragraph to
               find out you were being asked something. Now the ask and its
               buttons share the top line and everything explanatory sits
               under it, so the question is answerable at a glance and the
               detail is there when you want it. */
            <li key={p.id} className="rounded border border-amber-300 bg-amber-50/60 px-2 py-1.5 dark:border-amber-700 dark:bg-amber-900/10">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-200">
                  <strong className="font-medium">{SeatRole(p.written_by)}</strong>
                  {' proposes: '}
                  {/* WHAT IT CHANGES, not what it holds. A proposal carries the
                      whole merged map, so listing its axes would leave you
                      diffing two lists of eight by eye. */}
                  {/* NEVER INVENT A SUGGESTION. The fallback here used to list
                      the proposal's whole axis list whenever the summary came
                      back empty — so a pass that read 400 phrases and arrived
                      at the fifteen columns already standing rendered as
                      "proposes: axes buyer, comparison type, data source,
                      deployment, pricing model", five axes the room had had
                      all along. The backend no longer raises a proposal that
                      changes nothing; if one is somehow here, it says so. */}
                  <span className="text-gray-600 dark:text-gray-300">
                    {changeSummary(frame, p) || (
                      <em className="text-gray-500">nothing - this matches the setup already standing</em>
                    )}
                  </span>
                </span>
                {/* Who it is waiting on, as a chip rather than the tail of a
                    sentence — it is the difference between "you must act" and
                    "somebody else can". */}
                <span
                  className={`shrink-0 rounded-full px-1.5 text-[11px] ${
                    p.needs_human
                      ? 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100'
                      : 'text-amber-700 dark:text-amber-300'
                  }`}
                >
                  {p.needs_human ? 'removes something - needs you' : 'waiting on the fact checker, or you'}
                </span>
                <button type="button" onClick={() => onAccept(p.id)} className="shrink-0 rounded border border-emerald-300 px-1.5 py-0.5 text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/20">
                  Accept
                </button>
                <button type="button" onClick={() => onDismiss(p.id)} className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
                  Dismiss
                </button>
              </div>
              {/* The supporting detail, demoted. Never the thing you read first. */}
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                players: {p.vocab.player || '-'}
                {p.reason && <> · {p.reason}</>}
              </div>
              {/* THE COLUMNS THEMSELVES, when it is proposing any. "adds 12
                  columns" is the shape of the thing; whether `api support`
                  should have swallowed `API access` is the question, and it
                  cannot be answered from a count. */}
              {(() => {
                const d = specDiff(frame?.specs, p.specs);
                if (d.added.length === 0 && d.renamed.length === 0) return null;
                return (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`frame-specs-${p.id}`}>
                    {/* A RENAME READS AS A RENAME. Shown as a plain new chip
                        beside a vanished old one it looks like a column being
                        swapped out — and a column leaving takes its values with
                        it, which is the one thing to refuse. The arrow is the
                        whole message: same column, better name, every value
                        carried across. */}
                    {d.renamed.map(([was, now]) => {
                      const col = p.specs.find(s => s.attribute === now);
                      return (
                        <span
                          key={`${was}->${now}`}
                          title={`Everything recorded under "${was}" moves to "${now}" - nothing is lost.${col?.meaning ? ` ${col.meaning}` : ''}`}
                          className="rounded-full border border-sky-300 px-1.5 text-[11px] text-sky-900 dark:border-sky-700 dark:text-sky-200"
                          data-testid={`frame-rename-${now}`}
                        >
                          <span className="line-through opacity-60">{was}</span>
                          {' → '}
                          {now}
                        </span>
                      );
                    })}
                    {d.added.map(attr => {
                      const col = p.specs.find(s => s.attribute === attr);
                      return (
                        <span
                          key={attr}
                          title={col?.meaning || undefined}
                          className="rounded-full border border-amber-300 px-1.5 text-[11px] text-amber-900 dark:border-amber-700 dark:text-amber-200"
                        >
                          {attr}
                          {col?.unit && <span className="text-amber-600 dark:text-amber-400"> ({col.unit})</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
            </li>
            );
          })}
        </ul>
      )}

      {frame && !editing && (
        /* THREE THINGS, NOT ONE LIST. This ran the vocabulary, the axes and
           the reason together as one unbroken column of label/value pairs,
           with a violet label the only hint that `buyer` was a different kind
           of thing from `a product is`. They answer different questions —
           what this market IS, how the room SORTS it, what its products are
           COMPARED ON — so they get said separately. */
        <div className="mt-4 flex flex-col gap-4 text-gray-700 dark:text-gray-200" data-testid="frame-detail">
          {/* The market and the players are the headline above; the rest of
              the vocabulary is what the tables mean by their words. */}
          <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5">
            {VOCAB.filter(v => v.key !== 'market' && v.key !== 'player').map(v => (
              <div key={v.key} className="contents">
                <dt className="text-gray-400">{v.label}</dt>
                <dd>{frame.vocab[v.key] || '-'}</dd>
              </div>
            ))}
          </dl>
          {frame.axes.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">sorts on</div>
              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2">
                {frame.axes.map(a => (
                  <div key={a.dimension} className="contents">
                    <dt className="font-medium text-violet-700 dark:text-violet-300">{axisLabel(a.dimension)}</dt>
                    <dd className="flex flex-wrap gap-1">
                      {/* A MEANING THAT IS A LIST reads as the values it names. */}
                      {meaningChips(a.meaning).map(v => (
                        <span key={v} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {v}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {/* THE SHEET, which this panel never showed at all. Fifteen columns
              decide every cell of the feature matrix and you could not read
              them anywhere but the table itself. */}
          {frame.specs && frame.specs.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">compared on</div>
              <div className="flex flex-wrap gap-1" data-testid="frame-columns">
                {frame.specs.map(c => (
                  <span
                    key={c.attribute}
                    title={c.meaning || undefined}
                    className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    {c.attribute}
                    {c.unit && <span className="text-gray-400"> ({c.unit})</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* WHY IT WAS WRITTEN, said so. Bare, the first frame's reason ("No
              frame yet. Based on the description…") read as the room's state,
              right under the frame it produced. */}
          {frame.reason && (
            <blockquote className="border-l-2 border-gray-200 pl-3 text-gray-500 dark:border-gray-600 dark:text-gray-400">
              <span className="font-medium not-italic">Why it was written this way: </span>
              <span className="italic">{frame.reason}</span>
            </blockquote>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-1 flex flex-col gap-1 text-xs" data-testid="frame-editor">
          {VOCAB.map(v => (
            <label key={v.key} className="flex items-center gap-2">
              <span className="w-20 text-gray-400">{v.label}</span>
              <input
                aria-label={`Frame ${v.label}`}
                value={vocab[v.key] ?? ''}
                onChange={e => setVocab(x => ({ ...x, [v.key]: e.target.value }))}
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-0.5"
              />
            </label>
          ))}
          <label className="flex items-start gap-2">
            <span className="w-20 text-gray-400 pt-1">tag groups</span>
            <textarea
              aria-label="Frame axes"
              value={axes}
              onChange={e => setAxes(e.target.value)}
              rows={4}
              placeholder={'one per line · CUISINE: the kind of food served'}
              className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 font-mono"
            />
          </label>
          <div className="flex gap-2 pl-[5.5rem]">
            <button type="button" onClick={save} disabled={busy} className="px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
              Save setup
            </button>
            <button type="button" onClick={() => setEditing(false)} className="px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-500">
              Cancel
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <ol className="mt-1 flex flex-col gap-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid="frame-history">
          {shown.map(h => (
            <li key={h.id} className="flex items-baseline gap-2">
              <span className="text-gray-400 shrink-0">{h.since ? new Date(h.since).toLocaleDateString() : ''}</span>
              <span className="flex-1 min-w-0 truncate">
                {h.axes.map(a => axisLabel(a.dimension)).join(', ') || 'no tag groups'} · {h.vocab.player || '-'}
                {h.reason && <span className="text-gray-400"> · {h.reason}</span>}
                {h.proposed && <span className="text-amber-700"> · proposed</span>}
              </span>
              {frame?.id !== h.id && !h.proposed && (
                <button type="button" onClick={() => onUse(h.id)} className="shrink-0 text-indigo-600 dark:text-indigo-400 hover:underline">
                  use this setup
                </button>
              )}
              {frame?.id === h.id && <span className="shrink-0 text-emerald-600">current</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
